
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Zap, Moon, Smile, Leaf, Wind, Droplets } from 'lucide-react';
import sound from "../../../assets/Singing_B.png";

import { userApi } from '../../../config';
import { SEO } from '../../SEO';

const SoundTherapy = () => {
  const [userDetails, setUserDetails] = useState<any>(null);

  const fetchUserDetails = async () => {
    try {
      const token = sessionStorage.getItem("userAccessToken");
      if (token) {
        const res = await userApi.get<{user: any}>('/get-user-profile', { headers: { Authorization: `Bearer ${token}` } });
        setUserDetails(res.data.user);
      }
    } catch (error) {
      console.error('Failed to fetch user details', error);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);
  
  const sessionData = {
    _id: "sound-therapy-session-01",
    title: "Himalayan Singing Bowl Therapy",
    description: "A deep relaxation session using Himalayan singing bowls to balance your chakras.",
    price: 1500,
    duration: "60 minutes",
    category: "Sound Therapy",
    availableSlots: ["10:00 AM", "11:30 AM", "02:00 PM", "03:30 PM"],
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="bg-[#F8F7F4] font-sans text-gray-800">
      <SEO
        title="Sound Therapy & Himalayan Singing Bowl Healing | MetaFit Wellness"
        description="Experience transformative Sound Therapy and Himalayan Singing Bowl healing sessions with certified sound healers at MetaFit Wellness. Restore mental clarity and energy balance."
        canonical="/Sound-therapy"
        keywords="sound therapy, tibetan singing bowls, sound healing, vibrational therapy, chakra balancing, MetaFit Wellness"
      />
      {/* Hero Section */}
      <motion.section 
        className="relative bg-cover bg-no-repeat bg-center py-24 sm:py-32 px-4 bg-teal-900" 
        style={{ backgroundImage: `url('/sound.png')` }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <motion.div 
          className="relative max-w-4xl mx-auto text-center z-10"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight leading-tight mb-4">
            Harmonize Your Being with Sound
          </h1>
          <p className="text-lg md:text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Immerse yourself in therapeutic soundscapes to restore balance to your mind, body, and soul.
          </p>
          <Link
            to="/sound-therapy/details"
            className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-4 px-10 rounded-full transition duration-300 ease-in-out transform hover:scale-105 inline-flex items-center text-lg shadow-lg"
          >
            Book a Session
            <ChevronRight className="ml-2 h-5 w-5" />
          </Link>
        </motion.div>
      </motion.section>

      {/* Introduction Section */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={fadeIn}
            className="relative"
          >
            <img 
              src={sound} 
              alt="Sound Bath"
              className="rounded-xl shadow-2xl w-full h-auto object-cover"
            />
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={fadeIn}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What is Sound Therapy?
            </h2>
            <p className="text-gray-600 mb-4 text-lg leading-relaxed">
              Sound therapy is an ancient practice that uses tonal frequencies to bring the body into a state of vibrational balance and harmony. Through the use of instruments like crystal singing bowls and gongs, we create a soundscape that allows for deep relaxation and healing.
            </p>
            <p className="text-gray-600 mb-6 text-lg leading-relaxed">
              This immersive experience helps to quiet the mind, release tension, and connect you with a deeper sense of inner peace.
            </p>
            <Link 
              to="/about"
              className="text-teal-600 hover:text-teal-700 font-semibold text-lg transition duration-300 ease-in-out inline-flex items-center group"
            >
              Our Philosophy <ChevronRight className="ml-1 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-white py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={fadeIn}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Benefits of Sound Therapy
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
              Regularly participating in sound therapy can lead to profound positive changes.
            </p>
          </motion.div>
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
          >
            {[
              { icon: <Wind />, title: 'Stress Reduction', text: 'Calm your nervous system and significantly lower stress levels.' },
              { icon: <Moon />, title: 'Improved Sleep', text: 'Promote deeper, more restful sleep and combat insomnia.' },
              { icon: <Smile />, title: 'Emotional Balance', text: 'Release emotional blockages and cultivate a sense of well-being.' },
              { icon: <Zap />, title: 'Increased Energy', text: 'Recharge your body’s energy fields, leaving you feeling revitalized.' },
              { icon: <Leaf />, title: 'Enhanced Focus', text: 'Quiet mental chatter and improve concentration and clarity.' },
              { icon: <Droplets />, title: 'Deep Relaxation', text: 'Achieve a state of profound physical and mental relaxation.' },
            ].map((item, i) => (
              <motion.div key={i} variants={fadeIn} className="bg-gray-50/50 p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 flex items-start space-x-6">
                <div className="bg-teal-100 text-teal-600 p-4 rounded-full">
                  {React.cloneElement(item.icon, { className: "h-7 w-7" })}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={fadeIn}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Your Sound Journey
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
              Each session is a carefully guided experience from start to finish.
            </p>
          </motion.div>
          <motion.div 
            className="space-y-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
          >
            {[
              { title: '1. Arrival & Centering', text: 'We start with gentle breathwork to help you settle in and leave the outside world behind.' },
              { title: '2. Sound Immersion', text: 'You’ll lie down comfortably as a symphony of healing sounds washes over you, guiding you into a meditative state.' },
              { title: '3. Silence & Integration', text: 'After the sound bath, there is a period of silence to allow the vibrations to integrate and the healing to deepen.' },
              { title: '4. Gentle Return', text: 'We conclude with a gentle grounding exercise to bring your awareness back, leaving you refreshed and serene.' },
            ].map((step, i) => (
              <motion.div key={i} variants={fadeIn} className="flex items-start md:space-x-8">
                <div className="hidden md:flex flex-col items-center mr-8">
                  <div className="flex-shrink-0 w-16 h-16 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-2xl shadow-md">
                    {i + 1}
                  </div>
                  {i < 3 && <div className="mt-4 w-0.5 h-24 bg-gray-300"></div>}
                </div>
                <div className="bg-white p-8 rounded-xl shadow-lg flex-1">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 md:hidden">{i + 1}. {step.title.split('. ')[1]}</h3>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 hidden md:block">{step.title.split('. ')[1]}</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    {step.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      
      {/* Call to Action Section */}
      <section className="bg-white py-24 px-4">
        <motion.div 
          className="max-w-4xl mx-auto text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeIn}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Experience Harmony?
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Take the first step towards a more peaceful, balanced you. Our certified practitioners are here to guide you.
          </p>
          <Link
            to="/sound-therapy/details"
            className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-4 px-10 rounded-full transition duration-300 ease-in-out transform hover:scale-105 inline-flex items-center text-lg shadow-lg"
          >
            View Schedule & Book Now <ChevronRight className="ml-2 h-5 w-5" />
          </Link>
        </motion.div> 
      </section>

    </div>
  );
};

export default SoundTherapy;
